import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    Button,
    Grid,
    Divider,
    CircularProgress,
    Alert,
    Avatar,
    Card,
    CardContent,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Person as PersonIcon,
    Receipt as ReceiptIcon,
    RequestQuote as RequestQuoteIcon,
    AccountBalance as AccountBalanceIcon,
} from '@mui/icons-material';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import StatusChip from '../components/common/StatusChip';

const PRICE_LIST_LABELS = {
    mayorista_a: 'Mayorista A',
    mayorista_b: 'Mayorista B',
    premium: 'Premium',
    estandar: 'Estándar',
};

const CREDIT_STATUS_LABELS = {
    aprobado: { label: 'Aprobado', bg: '#e8f5e9', color: '#2e7d32' },
    revision: { label: 'En Revisión', bg: '#fff3e0', color: '#f57c00' },
    bloqueado: { label: 'Bloqueado', bg: '#ffebee', color: '#c62828' },
};

const formatMoney = (value) => `$${parseFloat(value || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const DataRow = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 1 }}>
        <Typography sx={{ color: '#4e7397', fontSize: '0.875rem' }}>{label}</Typography>
        <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', textAlign: 'right' }}>{value}</Typography>
    </Box>
);

const BillingConfirmation = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { logout } = useAuth();

    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [saving, setSaving] = useState(false);

    const fetchOrder = React.useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/api/orders/${id}/`);
            setOrder(response.data);
        } catch (error) {
            console.error('Error fetching order:', error);
            setError('Error al cargar los datos del pedido');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchOrder();
    }, [fetchOrder]);

    const handleConfirmInvoice = async () => {
        setSaving(true);
        setError('');
        try {
            await axios.patch(`/api/orders/${id}/`, { status: 'facturado' });
            setSuccess('¡Pedido facturado con éxito!');
            setTimeout(() => {
                navigate('/production');
            }, 2000);
        } catch (error) {
            setError(error.response?.data?.error || 'Error al registrar la facturación');
        } finally {
            setSaving(false);
        }
    };

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', minHeight: '100vh' }}>
                <Sidebar onLogout={handleLogout} />
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress />
                </Box>
            </Box>
        );
    }

    if (!order) {
        return (
            <Box sx={{ display: 'flex', minHeight: '100vh' }}>
                <Sidebar onLogout={handleLogout} />
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Alert severity="error">Pedido no encontrado</Alert>
                </Box>
            </Box>
        );
    }

    const client = order.client;
    const tango = client?.tango_info;
    const creditStatus = CREDIT_STATUS_LABELS[client?.credit_status] || null;
    const clientBlocked = client && (client.credit_status === 'bloqueado' || tango?.habilitado === false);
    const canInvoice = order.status === 'listo';

    return (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: '100vh', bgcolor: '#f6f7f8' }}>
            <Sidebar onLogout={handleLogout} />
            <Box sx={{ flex: 1, p: { xs: 2, md: 3 }, pt: { xs: 7, md: 3 } }}>
                {/* Breadcrumb / Back */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate(`/production/${id}`)}
                        sx={{ color: '#4e7397', textTransform: 'none', '&:hover': { bgcolor: 'transparent', color: '#1976d2' } }}
                    >
                        Volver al Pedido
                    </Button>
                </Box>

                <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                        <Typography variant="h4" sx={{ color: '#0e141b', fontWeight: 900, fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
                            Facturar Pedido #{order.order_number}
                        </Typography>
                        <StatusChip status={order.status} />
                    </Box>
                    <Typography sx={{ mb: 4, color: '#4e7397', fontSize: '0.875rem' }}>
                        Verifique los datos del cliente y el detalle del pedido antes de emitir la factura en Tango.
                    </Typography>

                    {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
                    {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

                    {clientBlocked && (
                        <Alert severity="warning" sx={{ mb: 3, fontWeight: 600 }}>
                            Atención: el cliente figura {client.credit_status === 'bloqueado' ? 'con crédito bloqueado' : 'como inhabilitado en Tango'}. Verifique antes de facturar.
                        </Alert>
                    )}

                    <Grid container spacing={3}>
                        {/* Client Info (Tango) */}
                        <Grid item xs={12} md={6}>
                            <Card sx={{ height: '100%', border: '1px solid #d0dbe7', boxShadow: 'none' }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                        <Avatar sx={{ bgcolor: '#1976d2' }}><PersonIcon /></Avatar>
                                        <Box>
                                            <Typography variant="h6" sx={{ fontWeight: 700 }}>Datos del Cliente</Typography>
                                            {client?.cod_cliente && (
                                                <Typography sx={{ fontSize: '0.75rem', color: '#4e7397' }}>
                                                    Código Tango: <strong>{client.cod_cliente}</strong>
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                    <Divider sx={{ mb: 2 }} />
                                    {client ? (
                                        <>
                                            <DataRow label="Razón Social" value={client.name} />
                                            <DataRow label="CUIT" value={client.cuit || 'N/A'} />
                                            <DataRow label="Contacto" value={client.contact_person || 'N/A'} />
                                            <DataRow label="Email" value={client.email || 'N/A'} />
                                            <DataRow label="Teléfono" value={client.phone || 'N/A'} />
                                            <DataRow label="Dirección" value={`${client.delivery_address || 'N/A'}${client.city ? `, ${client.city}` : ''}`} />
                                            <DataRow label="Lista de Precios" value={PRICE_LIST_LABELS[client.price_list] || client.price_list || 'N/A'} />
                                            {creditStatus && (
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 1, alignItems: 'center' }}>
                                                    <Typography sx={{ color: '#4e7397', fontSize: '0.875rem' }}>Estado de Crédito</Typography>
                                                    <Chip label={creditStatus.label} size="small" sx={{ bgcolor: creditStatus.bg, color: creditStatus.color, fontWeight: 700, fontSize: '0.7rem' }} />
                                                </Box>
                                            )}
                                        </>
                                    ) : (
                                        <Typography sx={{ color: '#4e7397' }}>Consumidor Final (sin cliente asociado)</Typography>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Tango Financial Info */}
                        <Grid item xs={12} md={6}>
                            <Card sx={{ height: '100%', border: '1px solid #d0dbe7', boxShadow: 'none' }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                        <Avatar sx={{ bgcolor: '#800020' }}><AccountBalanceIcon /></Avatar>
                                        <Typography variant="h6" sx={{ fontWeight: 700 }}>Cuenta Corriente (Tango)</Typography>
                                    </Box>
                                    <Divider sx={{ mb: 2 }} />
                                    {tango ? (
                                        <>
                                            <DataRow label="Condición de Venta" value={tango.condicion_venta || 'N/A'} />
                                            <DataRow label="Cupo de Crédito" value={formatMoney(tango.cupo_credito)} />
                                            <DataRow label="Saldo Cta. Cte." value={formatMoney(tango.saldo_cc)} />
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 1, alignItems: 'center' }}>
                                                <Typography sx={{ color: '#4e7397', fontSize: '0.875rem' }}>Habilitado en Tango</Typography>
                                                <Chip
                                                    label={tango.habilitado ? 'Habilitado' : 'Inhabilitado'}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: tango.habilitado ? '#e8f5e9' : '#ffebee',
                                                        color: tango.habilitado ? '#2e7d32' : '#c62828',
                                                        fontWeight: 700,
                                                        fontSize: '0.7rem'
                                                    }}
                                                />
                                            </Box>
                                            {tango.fecha_modi && (
                                                <DataRow label="Última Sincronización" value={new Date(tango.fecha_modi).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' })} />
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <DataRow label="Límite de Crédito" value={formatMoney(client?.credit_limit)} />
                                            <DataRow label="Saldo Actual" value={formatMoney(client?.current_balance)} />
                                            <DataRow label="Crédito Disponible" value={formatMoney(client?.available_credit)} />
                                            <Typography sx={{ mt: 2, color: '#4e7397', fontSize: '0.75rem', fontStyle: 'italic' }}>
                                                {client ? 'Cliente sin réplica de Tango disponible. Se muestran los datos locales.' : 'Venta al público: no hay cuenta corriente asociada.'}
                                            </Typography>
                                        </>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Order Detail */}
                        <Grid item xs={12}>
                            <Paper sx={{ p: { xs: 2, md: 4 }, border: '1px solid #d0dbe7', borderRadius: '0.75rem', boxShadow: 'none' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                                    <Avatar sx={{ bgcolor: '#00838f' }}><ReceiptIcon /></Avatar>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                        <Typography variant="h6" sx={{ fontWeight: 700 }}>Detalle del Pedido</Typography>
                                        <Chip
                                            label={order.order_type === 'wholesale' ? 'Negocio' : 'Público'}
                                            size="small"
                                            sx={{
                                                height: 20,
                                                fontSize: '0.7rem',
                                                fontWeight: 700,
                                                bgcolor: order.order_type === 'wholesale' ? '#ede7f6' : '#e0f7fa',
                                                color: order.order_type === 'wholesale' ? '#5e35b1' : '#00838f',
                                                borderRadius: '4px'
                                            }}
                                        />
                                    </Box>
                                </Box>

                                <TableContainer sx={{ mb: 3, border: '1px solid #e7edf3', borderRadius: '0.5rem', display: { xs: 'none', md: 'block' } }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: '#f6f7f8' }}>
                                                <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Producto</TableCell>
                                                <TableCell align="right" sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Solicitado</TableCell>
                                                <TableCell align="right" sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Peso Real (kg)</TableCell>
                                                <TableCell align="right" sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Precio Unit.</TableCell>
                                                <TableCell align="right" sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Subtotal</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(order.items || []).map((item) => (
                                                <TableRow key={item.id}>
                                                    <TableCell sx={{ fontWeight: 600 }}>{item.product?.name || 'Producto'}</TableCell>
                                                    <TableCell align="right">{parseFloat(item.quantity).toLocaleString('es-AR')} {item.product?.unit || ''}</TableCell>
                                                    <TableCell align="right">{item.actual_weight ? parseFloat(item.actual_weight).toLocaleString('es-AR') : '—'}</TableCell>
                                                    <TableCell align="right">{formatMoney(item.unit_price)}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600 }}>{formatMoney(item.subtotal)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {/* Mobile: Tarjetas de items */}
                                <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.5, mb: 3 }}>
                                    {(order.items || []).map((item) => (
                                        <Box key={item.id} sx={{ p: 1.5, border: '1px solid #e7edf3', borderRadius: '0.5rem' }}>
                                            <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', mb: 1 }}>{item.product?.name || 'Producto'}</Typography>
                                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                                                <Box>
                                                    <Typography sx={{ fontSize: '0.65rem', color: '#4e7397', textTransform: 'uppercase', fontWeight: 700 }}>Solicitado</Typography>
                                                    <Typography sx={{ fontSize: '0.8125rem' }}>{parseFloat(item.quantity).toLocaleString('es-AR')} {item.product?.unit || ''}</Typography>
                                                </Box>
                                                <Box>
                                                    <Typography sx={{ fontSize: '0.65rem', color: '#4e7397', textTransform: 'uppercase', fontWeight: 700 }}>Peso Real</Typography>
                                                    <Typography sx={{ fontSize: '0.8125rem' }}>{item.actual_weight ? `${parseFloat(item.actual_weight).toLocaleString('es-AR')} kg` : '—'}</Typography>
                                                </Box>
                                                <Box>
                                                    <Typography sx={{ fontSize: '0.65rem', color: '#4e7397', textTransform: 'uppercase', fontWeight: 700 }}>Precio Unit.</Typography>
                                                    <Typography sx={{ fontSize: '0.8125rem' }}>{formatMoney(item.unit_price)}</Typography>
                                                </Box>
                                                <Box>
                                                    <Typography sx={{ fontSize: '0.65rem', color: '#4e7397', textTransform: 'uppercase', fontWeight: 700 }}>Subtotal</Typography>
                                                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700 }}>{formatMoney(item.subtotal)}</Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, mb: 4 }}>
                                    <Typography sx={{ color: '#4e7397', fontSize: '0.875rem' }}>
                                        Peso Total: <strong>{order.total_weight} kg</strong>
                                    </Typography>
                                    <Typography sx={{ color: '#4e7397', fontSize: '0.875rem' }}>
                                        Subtotal: <strong>{formatMoney(order.subtotal)}</strong>
                                    </Typography>
                                    <Typography sx={{ fontWeight: 800, color: '#1b5e20', fontSize: '1.25rem' }}>
                                        Total: {formatMoney(order.total)}
                                    </Typography>
                                </Box>

                                {!canInvoice && !success && (
                                    <Alert severity="info" sx={{ mb: 3 }}>
                                        Este pedido no está pendiente de facturación (estado actual: {order.status_display || order.status}).
                                    </Alert>
                                )}

                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', sm: 'row' }, justifyContent: 'flex-end', gap: 2 }}>
                                    <Button
                                        variant="outlined"
                                        onClick={() => navigate(`/production/${id}`)}
                                        sx={{ textTransform: 'none', fontWeight: 700 }}
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        disabled={saving || !canInvoice}
                                        onClick={handleConfirmInvoice}
                                        startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <RequestQuoteIcon />}
                                        sx={{
                                            bgcolor: '#00838f',
                                            '&:hover': { bgcolor: '#006064' },
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            px: 4
                                        }}
                                    >
                                        Confirmar Facturación
                                    </Button>
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        </Box>
    );
};

export default BillingConfirmation;
